import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const spotlights = [
  {
    id: "hytera-bwv",
    href: "/hytera-bwv",
    badge: "Body-Worn Video",
    badgeIcon: "ri-camera-3-line",
    title: "Hytera BWC",
    subtitle: "Malaysia's Primary Body-Worn Video Solution",
    description: "Hytera body-worn cameras are the trusted standard for law enforcement, government agencies, and security operations across Malaysia — delivering encrypted evidence capture, real-time streaming, and seamless digital evidence management.",
    highlights: ["4K Ultra HD recording (SC880 5G Flagship)", "AES-256 encrypted storage", "Real-time live streaming to command", "Integrated Evidence Management System"],
    brands: ["Hytera"],
    stat: { value: "68%", label: "Reduction in evidence disputes" },
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/210918711682e061bc4ce59b63c4c38e.png",
    accentColor: "from-[#1a1a1a]/90",
    logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/6d2ef031-b05d-445b-add7-cb1ee7ec1599_Hytera-Logo-RGB.png?v=2b451d70b2f39a7a3313fb4cfd9d1d6a",
  },
  {
    id: "arcfra",
    href: "/arcfra-spotlight",
    badge: "HCI Infrastructure",
    badgeIcon: "ri-server-line",
    title: "Arcfra Platform",
    subtitle: "VMware & Nutanix Alternative for Malaysian Enterprises",
    description: "Arcfra delivers a complete hyperconverged infrastructure stack that replaces VMware vSphere and Nutanix AHV — with up to 60% lower TCO, no vendor lock-in, and full local support through SE Distribution.",
    highlights: ["Full HCI stack — compute, storage, networking", "Up to 60% lower TCO vs VMware", "NVMe-oF all-flash with sub-100µs latency", "No vendor lock-in, open standards"],
    brands: ["Arcfra"],
    stat: { value: "60%", label: "Lower TCO vs VMware" },
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/eacf02ed135400b71b85c128821966bc.jpeg",
    accentColor: "from-[#1a1a1a]/90",
    logo: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/e4390374f33642fbb291c106f3ac5eaa.jpeg",
  },
  {
    id: "exagrid",
    href: "/exagrid-spotlight",
    badge: "Backup Storage",
    badgeIcon: "ri-hard-drive-2-line",
    title: "ExaGrid Backup",
    subtitle: "Fastest Recovery & Ransomware-Proof Backup Storage",
    description: "ExaGrid is the only backup storage with a landing zone for instant recovery, scale-out architecture for no forklift upgrades, and a non-network-facing repository that stops ransomware from encrypting your backups.",
    highlights: ["Landing zone for instant VM recovery", "Non-network-facing ransomware protection", "10–50x data deduplication ratio", "Scale-out to 2.7PB per system"],
    brands: ["ExaGrid"],
    stat: { value: "2.7PB", label: "Max scale-out capacity" },
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4582f7169a276d758c8f6c6efbf274d8.jpeg",
    accentColor: "from-[#1a1a1a]/90",
    logo: "https://storage.readdy-site.link/project_files/15616acb-dd20-42a3-bb13-24e14d98a32e/332e9681-9df1-4dcd-93f2-cf1098c6260f_exagrid-logo.png?v=ddd934934df6f2e00eabba5539238300",
  },
  {
    id: "rugged",
    href: "/rugged-spotlight",
    badge: "Rugged Computing",
    badgeIcon: "ri-smartphone-line",
    title: "Rugged Devices",
    subtitle: "Honeywell & Dell — MIL-SPEC Field Computing",
    description: "Honeywell and Dell Rugged Pro deliver MIL-SPEC certified laptops, tablets, and handheld computers that survive drops, water, dust, and extreme temperatures — keeping your field teams productive in any environment.",
    highlights: ["MIL-STD-810H certified across full range", "IP67/IP68 dust and water protection", "Hot-swappable batteries for 24/7 ops", "3x longer lifespan vs consumer devices"],
    brands: ["Honeywell", "Dell Rugged Pro"],
    stat: { value: "3x", label: "Longer device lifespan" },
    image: "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/9f4667e91c4543f65aa96e91108ef808.jpeg",
    accentColor: "from-[#1a1a1a]/90",
    logo: "https://public.readdy.ai/ai/img_res/6c844463-0d58-479c-9deb-89f6b12df97c.png",
  },
];

const solutionCategories = [
  {
    icon: "ri-camera-3-line",
    title: "Body-Worn Video",
    href: "/hytera-bwv",
    brands: ["Hytera"],
  },
  {
    icon: "ri-cpu-line",
    title: "Rugged Computing",
    href: "/solutions/rugged-computing",
    brands: ["Honeywell", "Dell Rugged Pro"],
  },
  {
    icon: "ri-stack-line",
    title: "Hyperconverged Infrastructure",
    href: "/arcfra-spotlight",
    brands: ["Arcfra"],
  },
  {
    icon: "ri-hard-drive-3-line",
    title: "Enterprise Backup & Recovery",
    href: "/exagrid-spotlight",
    brands: ["ExaGrid", "Synology"],
  },
];

const whyChooseUs = [
  { icon: "ri-award-line", title: "Authorized Distributor", description: "Official authorized distributor for 3 brands (Hytera, Arcfra, ExaGrid) and technology partner for 3 more — genuine products, full warranty, direct manufacturer support." },
  { icon: "ri-tools-line", title: "Solutions Architecture", description: "We design integrated solutions tailored to your specific industry and operational requirements — not just off-the-shelf product sales." },
  { icon: "ri-customer-service-2-line", title: "Local Support", description: "Based in Kuala Lumpur with a dedicated support team available Monday to Friday, 9:30 AM – 6:30 PM (GMT+8)." },
  { icon: "ri-shield-check-line", title: "Proven Track Record", description: "500+ enterprise clients served across Malaysia with a 98% client retention rate — built on trust and measurable results." },
];

const solutionsStructuredData = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.sedistribution.com.my/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Solutions",
        "item": "https://www.sedistribution.com.my/solutions"
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Enterprise IT Solutions Malaysia | SE Distribution",
    "description": "SE Distribution is Malaysia's authorized distributor for Body-Worn Video (Hytera SC880 5G, SC780 4G, GC550), Rugged Computing (Honeywell, Dell Rugged Pro MIL-STD-810H), Hyperconverged Infrastructure (Arcfra — VMware & Nutanix alternative, 60% lower TCO), and Enterprise Backup & Recovery (ExaGrid ransomware-proof, Synology NAS). 500+ enterprise clients, KL-based support.",
    "url": "https://www.sedistribution.com.my/solutions",
    "publisher": {
      "@type": "Organization",
      "name": "SE Distribution",
      "url": "https://www.sedistribution.com.my"
    }
  }
];

export default function SolutionsOverview() {
  useSEO({
    title: "Enterprise IT Solutions Malaysia | Body-Worn Video, Rugged Computing, Arcfra HCI, ExaGrid Backup | SE Distribution",
    description: "SE Distribution — Malaysia's authorized distributor for 4 enterprise IT solution areas: Body-Worn Video (Hytera SC880 5G, SC780, GC550), Rugged Computing (Honeywell & Dell MIL-STD-810H), Hyperconverged Infrastructure (Arcfra — VMware & Nutanix alternative, 60% lower TCO), Enterprise Backup & Recovery (ExaGrid + Synology). 500+ clients. KL-based support.",
    canonical: "https://www.sedistribution.com.my/solutions",
    keywords: "enterprise IT solutions Malaysia, Hytera BWC Malaysia, Arcfra HCI Malaysia, ExaGrid backup Malaysia, rugged computing Malaysia, body worn camera Malaysia, VMware alternative Malaysia, hyperconverged infrastructure Malaysia, SE Distribution authorized distributor",
    structuredData: solutionsStructuredData,
  });
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">

        {/* Hero */}
        <section className="relative bg-[#1a1a1a] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20technology%20enterprise%20solutions%20collage%20security%20cameras%20rugged%20devices%20data%20storage%20systems%20professional%20dark%20background%20dramatic%20lighting%20photorealistic%20commercial%20photography%20wide%20angle&width=1400&height=600&seq=solv-hero2&orientation=landscape"
            alt="Enterprise IT Solutions Malaysia — Body-Worn Video, Rugged Computing, HCI Infrastructure, Backup Storage"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a]/60 via-[#1a1a1a]/50 to-[#1a1a1a]/95"></div>

          {/* Top hero content */}
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-14 text-center">
            <span className="text-xs font-semibold tracking-widest uppercase text-white/50 mb-4 block">Authorized Enterprise IT Distributor — Malaysia</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
              Integrated Technology <strong>Solutions for Malaysian Enterprises</strong>
            </h1>
            <p className="text-white/65 text-sm max-w-2xl mx-auto leading-relaxed mb-4">
              SE Distribution is Malaysia&apos;s authorized distributor for four specialized enterprise IT solution areas — Body-Worn Video, Rugged Computing, Hyperconverged Infrastructure, and Enterprise Backup &amp; Recovery. Each solution area combines best-in-class brands into a cohesive, results-driven system built for your industry.
            </p>
            <p className="text-white/40 text-xs max-w-xl mx-auto leading-relaxed mb-10">
              Serving 500+ enterprise clients across Malaysia &middot; KL-based support team &middot; 98% client retention rate
            </p>
          </div>

          {/* 4 Solution Area Cards */}
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  icon: "ri-camera-3-line",
                  label: "Body-Worn Video",
                  brands: "Hytera SC880, SC780, GC550",
                  desc: "4K body cameras, live streaming & AES-256 encrypted evidence management for law enforcement & security.",
                  href: "/hytera-bwv",
                  stat: "68%",
                  statLabel: "Fewer evidence disputes",
                },
                {
                  icon: "ri-cpu-line",
                  label: "Rugged Computing",
                  brands: "Honeywell · Dell Rugged Pro",
                  desc: "MIL-STD-810H laptops, tablets & handhelds built for warehouses, logistics, field services & defence.",
                  href: "/solutions/rugged-computing",
                  stat: "3×",
                  statLabel: "Longer device lifespan",
                },
                {
                  icon: "ri-stack-line",
                  label: "Hyperconverged Infrastructure",
                  brands: "Arcfra AECP (VMware Alternative)",
                  desc: "Full-stack HCI replacing VMware & Nutanix — AVE, ABS, AKE, ANS, AFS. Gartner-recognized, up to 60% lower TCO.",
                  href: "/vmware-alternative-malaysia",
                  stat: "60%",
                  statLabel: "Lower TCO vs VMware",
                },
                {
                  icon: "ri-hard-drive-3-line",
                  label: "Enterprise Backup & Recovery",
                  brands: "ExaGrid · Synology",
                  desc: "Ransomware-proof backup with instant VM recovery, scale-out to 2.7PB, and time-locked delayed deletes.",
                  href: "/exagrid-backup-malaysia",
                  stat: "2.7PB",
                  statLabel: "Max scale-out capacity",
                },
              ].map((area) => (
                <Link
                  key={area.label}
                  to={area.href}
                  className="group bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 hover:border-white/25 transition-all cursor-pointer flex flex-col gap-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg flex-shrink-0">
                      <i className={`${area.icon} text-white text-lg`}></i>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-black text-white leading-none">{area.stat}</p>
                      <p className="text-white/40 text-[10px] leading-tight mt-0.5">{area.statLabel}</p>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-white mb-1 leading-snug">{area.label}</h2>
                    <p className="text-white/40 text-[10px] font-medium uppercase tracking-wider mb-2">{area.brands}</p>
                    <p className="text-white/60 text-xs leading-relaxed">{area.desc}</p>
                  </div>
                  <div className="mt-auto flex items-center gap-1.5 text-white/50 text-xs font-medium group-hover:text-white/80 transition-colors">
                    Explore <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#111] py-8 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "6", label: "Brand Partners" },
              { value: "500+", label: "Enterprise Clients" },
              { value: "4", label: "Solution Areas" },
              { value: "98%", label: "Client Retention" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</p>
                <p className="text-xs text-white/40 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Spotlight Cards — Large Featured Layout */}
        <section className="py-20 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Product Spotlights</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Explore Our Solution Spotlights</h2>
            <p className="text-gray-500 text-sm mt-3 max-w-xl mx-auto">Each spotlight page gives you deep-dive product specs, use cases, and deployment guidance for that solution area.</p>
          </div>

          <div className="space-y-8">
            {spotlights.map((s, idx) => (
              <div
                key={s.id}
                className={`flex flex-col ${idx % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"} gap-0 rounded-2xl overflow-hidden border border-gray-100 group`}
              >
                {/* Image Side */}
                <div className="relative w-full lg:w-1/2 h-64 lg:h-auto min-h-[280px] overflow-hidden flex-shrink-0">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-r ${idx % 2 === 1 ? "from-transparent to-[#1a1a1a]/60" : "from-[#1a1a1a]/60 to-transparent"}`}></div>
                  {/* Stat Badge */}
                  <div className="absolute bottom-5 left-5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3">
                    <p className="text-2xl font-bold text-white">{s.stat.value}</p>
                    <p className="text-white/70 text-xs">{s.stat.label}</p>
                  </div>
                </div>

                {/* Content Side */}
                <div className="w-full lg:w-1/2 bg-white p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
                      <i className={`${s.badgeIcon} text-[#1a1a1a] text-sm`}></i>
                    </div>
                    <span className="text-xs font-semibold tracking-widest uppercase text-gray-400">{s.badge}</span>
                  </div>

                  {/* Brand Logo */}
                  <div className="mb-4 h-7 flex items-center">
                    <img
                      src={s.logo}
                      alt={s.title}
                      className="h-6 w-auto max-w-[120px] object-contain"
                      style={{ filter: s.id === "arcfra" ? "none" : "brightness(0)" }}
                    />
                  </div>

                  <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">{s.title}</h2>
                  <p className="text-sm font-medium text-gray-400 mb-4">{s.subtitle}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-6">{s.description}</p>

                  {/* Highlights */}
                  <ul className="space-y-2 mb-6">
                    {s.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <i className="ri-check-line text-[#1a1a1a] mt-0.5 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>

                  {/* Brand Pills */}
                  <div className="flex flex-wrap gap-2 mb-7">
                    {s.brands.map((b) => (
                      <span key={b} className="text-xs bg-[#1a1a1a]/5 text-[#1a1a1a] px-3 py-1.5 rounded-full font-medium">{b}</span>
                    ))}
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <Link
                      to={s.href}
                      className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      View {s.title} Spotlight
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 text-sm font-medium px-6 py-3 rounded-md hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Enquire
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Solution Categories */}
        <section className="bg-[#f9f7f4] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center mb-12">
              <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Solution Categories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a]">Four Core Solution Areas</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">Our spotlights roll up into four integrated solution categories — each with a dedicated deep-dive page.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "ri-camera-3-line",
                  title: "Body-Worn Video",
                  desc: "Body-worn cameras and evidence management from Hytera — SC880 5G, SC780 4G, and GC550 for law enforcement and enterprise security.",
                  href: "/hytera-bwv",
                  brands: ["Hytera"],
                  image: "https://readdy.ai/api/search-image?query=security%20officer%20wearing%20body%20camera%20on%20patrol%20urban%20street%20at%20night%20professional%20law%20enforcement%20equipment%20tactical%20gear%20cinematic%20lighting%20realistic%20photography%20high%20detail&width=600&height=400&seq=cat-surv&orientation=landscape",
                },
                {
                  icon: "ri-cpu-line",
                  title: "Rugged Computing",
                  desc: "MIL-SPEC laptops, tablets, and handheld computers from Honeywell and Dell Rugged Pro for field operations.",
                  href: "/solutions/rugged-computing",
                  brands: ["Honeywell", "Dell Rugged Pro"],
                  image: "https://readdy.ai/api/search-image?query=warehouse%20worker%20scanning%20barcode%20handheld%20rugged%20device%20picking%20inventory%20industrial%20environment%20dramatic%20lighting%20realistic%20photography%20high%20detail%20logistics%20operations&width=600&height=400&seq=cat-rugged&orientation=landscape",
                },
                {
                  icon: "ri-stack-line",
                  title: "Hyperconverged Infrastructure",
                  desc: "AI-ready HCI platform from Arcfra — the cost-effective VMware & Nutanix alternative for Malaysian enterprises.",
                  href: "/vmware-alternative-malaysia",
                  brands: ["Arcfra"],
                  image: "https://readdy.ai/api/search-image?query=IT%20engineer%20working%20in%20server%20room%20data%20center%20maintaining%20hyperconverged%20infrastructure%20racks%20enterprise%20dramatic%20lighting%20high%20detail%20professional%20photography%20realistic&width=600&height=400&seq=cat-hci&orientation=landscape",
                },
                {
                  icon: "ri-hard-drive-3-line",
                  title: "Enterprise Backup & Recovery",
                  desc: "Ransomware-proof, scale-out backup storage from ExaGrid and Synology — fastest recovery, no forklift upgrades.",
                  href: "/exagrid-spotlight",
                  brands: ["ExaGrid", "Synology"],
                  image: "https://readdy.ai/api/search-image?query=enterprise%20data%20backup%20storage%20server%20hardware%20clean%20white%20equipment%20modern%20data%20center%20professional%20commercial%20photography%20sharp%20focus%20minimal%20background%20high%20detail%20technology%20rack&width=600&height=400&seq=cat-backup&orientation=landscape",
                },
              ].map((cat) => (
                <Link
                  key={cat.title}
                  to={cat.href}
                  className="group rounded-xl overflow-hidden border border-gray-100 bg-white hover:border-[#1a1a1a]/20 transition-all cursor-pointer"
                >
                  <div className="w-full h-44 overflow-hidden">
                    <img src={cat.image} alt={cat.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
                        <i className={`${cat.icon} text-[#1a1a1a] text-sm`}></i>
                      </div>
                      <h3 className="font-bold text-[#1a1a1a] text-base">{cat.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">{cat.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {cat.brands.map((b) => (
                        <span key={b} className="text-xs bg-[#1a1a1a]/5 text-[#1a1a1a] px-2.5 py-1 rounded-full font-medium">{b}</span>
                      ))}
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a1a1a] group-hover:gap-2.5 transition-all">
                      Explore Solutions <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose SE Distribution */}
        <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3 block">Why SE Distribution</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a]">The Authorized Distributor Advantage</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyChooseUs.map((w) => (
              <div key={w.title} className="bg-[#f9f7f4] rounded-lg p-6 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                  <i className={`${w.icon} text-[#1a1a1a] text-xl`}></i>
                </div>
                <h3 className="font-semibold text-[#1a1a1a] text-sm mb-2">{w.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{w.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#1a1a1a] py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Not Sure Which Solution Fits?</h2>
            <p className="text-white/60 text-sm mb-8 max-w-lg mx-auto leading-relaxed">
              Our solutions architects will assess your requirements and recommend the right combination of products and brands for your specific needs — at no obligation.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/contact"
                className="bg-white text-[#1a1a1a] text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Talk With Us
              </Link>
              <Link
                to="/brands"
                className="border border-white/40 text-white text-sm font-medium px-8 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                View All Brands
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
